import React, { useState, useCallback, useRef, useEffect, useMemo } from "react"
import { Handle, Position, useNodes, useEdges } from "reactflow"
import "./Node.css"
import {
  CheckSquare,
  Calendar,
  Plus,
  Minus,
  MoreHorizontal,
  Table2,
  Clock,
  ChevronDown,
  DollarSign,
  X,
  User,
  Globe,
  CheckCircle,
  Check,
  CheckCheck,
  Star,
  Trophy,
  Award,
  Zap,
  Target,
  Flag,
  Heart,
  ThumbsUp,
  Smile,
  PartyPopper,
  ArrowBigRight,
  Trash2,
  AlertTriangle,
  FileText,
} from "lucide-react"
import "./TaskNode.css"
import "./VariableReference.css"
import { useWorkflow } from "@/lib/contexts/workflow-context"
import { useUsers } from "@/lib/contexts/user-context"
import { router } from "@inertiajs/react"
import VariableReference from "./VariableReference"
import EnhancedVariableReference from "./EnhancedVariableReference"
import { collectUpstreamFormFieldVariables, hasUpstreamTaskNode } from "@/lib/workflowFormFieldVariables"
import { useWorkflowGraphForVariableDiscovery } from "@/hooks/useWorkflowGraphForVariableDiscovery"
import { getSystemVariablesForContext } from "@/lib/workflowSystemVariables"
import { shouldOfferAllowMultipleTaskInstances } from "@/lib/workflowLoopDetection"
import {
  DEFAULT_DYNAMIC_DATE_FORMAT,
  DYNAMIC_DATE_FORMAT_PRESETS,
  normalizeDynamicDateFormatPreset,
} from "@/lib/dateUtils"
import Checklist from "@/components/ui/Checklist"
import CopyPasteButtons from "./CopyPasteButtons"
import AssignmentSelector from "./AssignmentSelector"
import DynamicDateSelector from "./DynamicDateSelector"



const TaskNode = ({ data, id, users, categories = [], departments = [], integrations = [], onChecklistInteraction, csrf_token }) => {
  const { currentWorkflow, workflows, deleteNode, updateNode } = useWorkflow()
  const resolvedWorkflowId = currentWorkflow?.id ?? data?.workflowId ?? null
  const stepDatabaseId =
    data?.databaseId ??
    (typeof id === "string" && id.startsWith("step-") ? id.replace("step-", "") : null)
  // Prefer live React Flow graph for variable discovery; context is fallback only.
  const { nodes, edges } = useWorkflowGraphForVariableDiscovery(resolvedWorkflowId)
  const currentWorkflowData = workflows.find((w) => w.id === currentWorkflow?.id) ?? currentWorkflow
  const liveNodes = useNodes()
  const liveEdges = useEdges()
  const { expanded = true } = data
  
  // Helper function to ensure string values are never null
  const ensureString = (value) => value === null || value === undefined ? "" : String(value)
  
  // Helper function to format date for HTML date input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    
    // If it's already in YYYY-MM-DD format, return as is
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    
    // If it's a date string with time, extract just the date part
    if (typeof dateString === 'string' && dateString.includes(' ')) {
      return dateString.split(' ')[0];
    }

    if (dateString.includes('{{')) {
      return dateString;
    }
    
    // If it's a Date object or ISO string, convert to YYYY-MM-DD
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      
      // Use UTC methods to avoid timezone issues
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date for input:', error);
      return "";
    }
  }
  
  // Helper function to check if a value is a dynamic template string
  const isTemplateString = (value) => {
    if (value && containsExtractedVariables(value)) {
      return true;
    }
    return value && typeof value === 'string' && value.startsWith('~') && value.endsWith('~')
  }

  // Helper to extract template from due date
  const extractTemplateFromDue = (dueValue) => {
    if (dueValue && containsExtractedVariables(dueValue)) {
      return dueValue;
    }
    if (dueValue && isTemplateString(dueValue)) {
      return dueValue.slice(1, -1) // Remove ~ characters
    }
    return ""
  }

  const parseCustomTemplate = (template) => {
    if (!template || !template.startsWith("custom_")) {
      return null
    }

    const parts = template.split("_")
    if (parts.length < 3) {
      return null
    }

    const amount = parseInt(parts[1], 10)
    if (isNaN(amount)) {
      return null
    }

    return {
      amount,
      period: parts.slice(2).join("_"),
    }
  }

  const formatCustomPeriodLabel = (period, amount) => {
    if (period === "business_days" || period === "business_day") {
      return amount === 1 ? "business day" : "business days"
    }

    return period.replace(/_/g, " ")
  }

  // Helper to check if a string contains variables
  const containsExtractedVariables = (value) => {
    return value && typeof value === 'string' && value.includes('{{') && value.includes('}}')
  }

  // Get available variables from all connected request nodes throughout the workflow
  const getAvailableVariables = () => {
    if (!nodes || !edges) return []
    
    const availableVariables = []
    const visitedNodes = new Set()
    
    // Recursive function to find all upstream request nodes
    const findUpstreamRequestNodes = (nodeId) => {
      if (visitedNodes.has(nodeId)) return
      visitedNodes.add(nodeId)
      
      const node = nodes.find(n => n.id === nodeId)
      if (!node) return
      
      // If this is a request node, add its extracted variables
      if (node.type === 'requestNode' && node.data && node.data.extractedVariables && Array.isArray(node.data.extractedVariables)) {
        // Filter out any malformed variables
        const validVariables = node.data.extractedVariables.filter(variable => 
          variable && variable.name && typeof variable.name === 'string'
        )
        availableVariables.push(...validVariables)
      }
      
      // Find all incoming edges to this node
      const incomingEdges = edges.filter(edge => edge.target === nodeId)
      
      // Recursively check all source nodes
      incomingEdges.forEach(edge => {
        findUpstreamRequestNodes(edge.source)
      })
    }
    
    // Start from the current task node
    findUpstreamRequestNodes(id)
    
    return availableVariables
  }

  const getAvailableFormFields = () => collectUpstreamFormFieldVariables(nodes, edges, id)

  const systemVariables = useMemo(
    () => getSystemVariablesForContext({
      hasUpstreamCompletedTask: hasUpstreamTaskNode(nodes, edges, id),
    }),
    [nodes, edges, id]
  )

  // Get all available variables including form fields
  const getAllAvailableVariables = () => {
    const requestVariables = getAvailableVariables()
    const formFields = getAvailableFormFields()
    
    // Ensure request variables have the expected structure
    const validRequestVariables = requestVariables.filter(variable => 
      variable && variable.name && typeof variable.name === 'string'
    )
    
    const allVariables = [...validRequestVariables, ...formFields]
    
    return allVariables
  }

  // Get connected request nodes for display
  const getConnectedRequestNodes = () => {
    const connectedRequests = []
    const incomingEdges = edges.filter(edge => edge.target === id)
    
    incomingEdges.forEach(edge => {
      const sourceNode = nodes.find(node => node.id === edge.source)
      if (sourceNode && sourceNode.type === 'requestNode') {
        connectedRequests.push({
          id: sourceNode.id,
          name: sourceNode.data.name || 'HTTP Request',
          url: sourceNode.data.url || ''
        })
      }
    })
    
    return connectedRequests
  }

  // Get available form nodes for post-completion form selection
  const getAvailableFormNodes = () => {
    const formNodes = []
    
    nodes.forEach(node => {
      if (node.type === 'formNode') {
        formNodes.push({
          id: node.id,
          name: node.data.name || 'Dynamic Form',
          description: node.data.description || ''
        })
      }
    })
    
    return formNodes
  }

  // Check if task requires post-completion form but isn't connected to a form node
  const hasFormValidationError = () => {
    if (!taskData.requirePostCompletionForm) {
      return false
    }

    // Prefer live ReactFlow graph data to avoid stale workflow-context snapshots.
    const contextOutgoingEdges = edges.filter((edge) => edge.source === id)
    const liveOutgoingEdges = liveEdges.filter((edge) => edge.source === id)
    const outgoingEdges = liveOutgoingEdges.length > 0 ? liveOutgoingEdges : contextOutgoingEdges

    const hasConnectedFormNode = outgoingEdges.some((edge) => {
      const targetNode = nodes.find((node) => node.id === edge.target)
      return targetNode && targetNode.type === "formNode"
    })

    return !hasConnectedFormNode
  }

  const getConnectedPostCompletionFormId = useCallback(() => {
    const contextOutgoingEdges = edges.filter((edge) => edge.source === id)
    const liveOutgoingEdges = liveEdges.filter((edge) => edge.source === id)
    const outgoingEdges = liveOutgoingEdges.length > 0 ? liveOutgoingEdges : contextOutgoingEdges

    for (const edge of outgoingEdges) {
      const targetNode = nodes.find((node) => node.id === edge.target)
      if (!targetNode || targetNode.type !== "formNode") continue

      const candidate = targetNode.data?.form_id ?? targetNode.data?.formId ?? null
      if (candidate !== null && candidate !== undefined && String(candidate).trim() !== "") {
        return String(candidate).trim()
      }
    }

    return ""
  }, [edges, id, liveEdges, nodes])

  const [isHovering, setIsHovering] = useState(false)
  const [showMinimizeTooltip, setShowMinimizeTooltip] = useState(false)
  const [showSquareTooltip, setShowSquareTooltip] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [activeTab, setActiveTab] = useState("task") // "task", "settings", or "checklist"
  
  // Initialize UI state based on whether we have a template string
  const initialTemplate = extractTemplateFromDue(data.due)
  const hasTemplate = Boolean(initialTemplate)
  
  // Determine initial date picker type based on data
  const getInitialDatePickerType = () => {
    // If data.datePickerType exists, use it
    if (data.datePickerType) {
      return data.datePickerType
    }
    // If we have a template string, use dynamic
    if (hasTemplate) {
      return "dynamic"
    }
    // If we have a regular date (not template), use calendar
    if (data.due && !isTemplateString(data.due) && !containsExtractedVariables(data.due)) {
      return "calendar"
    }
    // Default to dynamic
    return "dynamic"
  }
  
  const [datePickerType, setDatePickerType] = useState(getInitialDatePickerType())
  const [dynamicDateOption, setDynamicDateOption] = useState(
    hasTemplate && !initialTemplate.startsWith('custom_') && !initialTemplate.startsWith('custom_day_of_month_') ? initialTemplate : (hasTemplate && (initialTemplate.startsWith('custom_') || initialTemplate.startsWith('custom_day_of_month_')) ? "custom" : "today")
  )
  const [customDays, setCustomDays] = useState(() => {
    if (hasTemplate && initialTemplate.startsWith('custom_day_of_month_')) {
      // Handle day_of_month format: custom_day_of_month_15
      const day = parseInt(initialTemplate.replace('custom_day_of_month_', ''))
      return isNaN(day) ? 1 : day
    } else if (hasTemplate && initialTemplate.startsWith('custom_')) {
      const parsedTemplate = parseCustomTemplate(initialTemplate)
      return parsedTemplate ? parsedTemplate.amount : 1
    }
    return 1
  })
  const [customPeriod, setCustomPeriod] = useState(() => {
    if (hasTemplate && initialTemplate.startsWith('custom_day_of_month_')) {
      return "day_of_month"
    } else if (hasTemplate && initialTemplate.startsWith('custom_')) {
      const parsedTemplate = parseCustomTemplate(initialTemplate)
      return parsedTemplate ? parsedTemplate.period : "days"
    }
    return "days"
  })
  const [showCustomInput, setShowCustomInput] = useState(
    hasTemplate && (initialTemplate.startsWith('custom_') || initialTemplate.startsWith('custom_day_of_month_'))
  )
  const [taskType, setTaskType] = useState(data.taskType || "standard")
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false)
  const userSetDatePickerType = useRef(false)

  const [taskData, setTaskData] = useState(() => {
    // Extract settings from data.settings or use defaults
    const settings = data.settings || {}
    
    return {
      name: ensureString(data.name),
      description: ensureString(data.description),
      link: ensureString(data.link),
      link_title: ensureString(data.link_title),
      category: ensureString(data.category) || "General",
      priority: ensureString(data.priority) || "5",
      due: data.due && containsExtractedVariables(data.due) ? data.due : (data.due ? formatDateForInput(data.due) : "~today~"),
      status: ensureString(data.status) || "To Do",
      deferrable: data.deferrable || 0,
      missable: data.missable || 0,
      dynamicDueDate: ensureString(data.dynamicDueDate) || extractTemplateFromDue(data.due) || "today",
      showProbabilityScale: data.showProbabilityScale || 1,
      taskTypeCategories: data.taskTypeCategories || [],
      assignee_id: data.assignee_id !== undefined && data.assignee_id !== null ? ensureString(data.assignee_id) : "",
      assignment: data.assignment || { type: 'individual', user_ids: [], department_ids: [], shared: true },
      checklist: data.checklist || [],
      datePickerType: data.datePickerType || "dynamic",
      // Settings for completion button and form - read from settings column first, then fallback to data properties
      completionButtonText: settings.completion_button_text !== undefined && settings.completion_button_text !== null 
        ? ensureString(settings.completion_button_text) 
        : (data.completionButtonText !== undefined && data.completionButtonText !== null 
            ? ensureString(data.completionButtonText) 
            : "Complete Task"),
      completionButtonColor: settings.completion_button_color !== undefined && settings.completion_button_color !== null 
        ? ensureString(settings.completion_button_color) 
        : (data.completionButtonColor !== undefined && data.completionButtonColor !== null 
            ? ensureString(data.completionButtonColor) 
            : "#10b981"),
      completionButtonIcon: settings.completion_button_icon !== undefined && settings.completion_button_icon !== null 
        ? ensureString(settings.completion_button_icon) 
        : (data.completionButtonIcon !== undefined && data.completionButtonIcon !== null 
            ? ensureString(data.completionButtonIcon) 
            : "CheckCircle"),
      requirePostCompletionForm: settings.require_post_completion_form !== undefined 
        ? settings.require_post_completion_form 
        : (data.requirePostCompletionForm !== undefined 
            ? data.requirePostCompletionForm 
            : false),
      postCompletionFormId: settings.post_completion_form_id !== undefined && settings.post_completion_form_id !== null 
        ? ensureString(settings.post_completion_form_id) 
        : (data.postCompletionFormId !== undefined && data.postCompletionFormId !== null 
            ? ensureString(data.postCompletionFormId) 
            : ""),
      captureDeferDateInPostCompletionForm:
        settings.capture_defer_date_in_post_completion_form !== undefined
          ? Boolean(settings.capture_defer_date_in_post_completion_form)
          : Boolean(data.captureDeferDateInPostCompletionForm),
      postCompletionDeferDateFieldId:
        settings.post_completion_defer_date_field_id !== undefined &&
        settings.post_completion_defer_date_field_id !== null
          ? ensureString(settings.post_completion_defer_date_field_id)
          : ensureString(data.postCompletionDeferDateFieldId ?? ""),
      allowMultipleInstances: Boolean(
        settings.allow_multiple_instances ?? data.allowMultipleInstances ?? false,
      ),
      dynamicDateFormat: data.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
    }
  })

  const showAllowMultipleInstancesOption = useMemo(
    () => shouldOfferAllowMultipleTaskInstances(id, liveEdges.length > 0 ? liveEdges : edges),
    [id, liveEdges, edges],
  )

  const [postCompletionFormDateFields, setPostCompletionFormDateFields] = useState([])

  // Refetch /forms/:id when the linked form node's template changes on the canvas (e.g. new date field),
  // not only when postCompletionFormId flips — the API is the source of truth, but the effect must re-run.
  const postCompletionFormCanvasFingerprint = useMemo(() => {
    const formId = String(taskData.postCompletionFormId || "").trim()
    if (!formId || !Array.isArray(nodes) || nodes.length === 0) {
      return ""
    }
    const formNode = nodes.find(
      (n) =>
        n.type === "formNode" &&
        String(n.data?.form_id ?? "").trim() === formId
    )
    const fields = formNode?.data?.fields
    if (!Array.isArray(fields)) {
      return `no-fields|${formId}`
    }
    try {
      return JSON.stringify(
        fields.map((f) => ({
          id: f?.id,
          type: f?.type,
          label: f?.label,
        }))
      )
    } catch {
      return `len:${fields.length}|${formId}`
    }
  }, [nodes, taskData.postCompletionFormId])

  // Handle updates when data.due changes (for when switching between workflows)
  useEffect(() => {
    const newTemplate = extractTemplateFromDue(data.due)
    const currentTemplate = extractTemplateFromDue(taskData.due)
    
    // Only update if the template actually changed
    if (newTemplate !== currentTemplate) {
      if (newTemplate) {
        // Update to new template
        if (newTemplate.startsWith('custom_day_of_month_')) {
          // Handle day_of_month format
          const day = parseInt(newTemplate.replace('custom_day_of_month_', ''))
          setDynamicDateOption('custom')
          setCustomDays(isNaN(day) ? 1 : day)
          setCustomPeriod('day_of_month')
          setShowCustomInput(true)
        } else if (newTemplate.startsWith('custom_')) {
          const parsedTemplate = parseCustomTemplate(newTemplate)
          if (parsedTemplate) {
            setDynamicDateOption('custom')
            setCustomDays(parsedTemplate.amount)
            setCustomPeriod(parsedTemplate.period)
            setShowCustomInput(true)
          }
        } else {
          setDynamicDateOption(newTemplate)
          setShowCustomInput(false)
        }
        setDatePickerType('dynamic')
        setTaskData(prev => ({ ...prev, due: data.due, dynamicDueDate: newTemplate }))
      } else {
        // No template, but keep dynamic as default
        setDatePickerType('dynamic')
        setDynamicDateOption('')
        setShowCustomInput(false)
        setTaskData(prev => ({ ...prev, due: data.due, dynamicDueDate: '' }))
      }
    }
  }, [data.due, id]) // Trigger when data.due changes or component mounts/changes

  // Handle updates when data prop changes (for when page refreshes or data is loaded)
  useEffect(() => {
    // Extract settings from data.settings or use defaults
    const settings = data.settings || {}
    
    setTaskData(prev => {
      // Only update due date if it's actually different to prevent overriding user changes
      const shouldUpdateDue = data.due !== undefined && data.due !== prev.due;
      const formattedDue = shouldUpdateDue 
        ? (data.due && containsExtractedVariables(data.due) ? data.due : formatDateForInput(data.due))
        : prev.due;
      
      const newData = {
        ...prev,
        name: ensureString(data.name),
        description: ensureString(data.description),
        link: ensureString(data.link),
        link_title: ensureString(data.link_title),
        category: ensureString(data.category) || "General",
        priority: ensureString(data.priority) || "5",
        due: formattedDue,
        status: ensureString(data.status) || "To Do",
        deferrable: data.deferrable || 0,
        missable: data.missable || 0,
        taskTypeCategories: data.taskTypeCategories || [],
        assignee_id: data.assignee_id !== undefined && data.assignee_id !== null ? ensureString(data.assignee_id) : "",
        // Only update assignment if data.assignment exists and is different from current
        assignment: data.assignment && data.assignment !== prev.assignment ? data.assignment : prev.assignment,
        checklist: data.checklist || [],
        dynamicDueDate: ensureString(data.dynamicDueDate) || extractTemplateFromDue(data.due) || "today",
        datePickerType: data.datePickerType || "dynamic",
        showProbabilityScale: data.showProbabilityScale || 1,
        // Settings for completion button and form - read from settings column
        // Only set defaults if the value is truly undefined/null, not if it's an empty string
        completionButtonText: settings.completion_button_text !== undefined && settings.completion_button_text !== null 
          ? ensureString(settings.completion_button_text) 
          : (prev.completionButtonText || "Complete Task"),
        completionButtonColor: settings.completion_button_color !== undefined && settings.completion_button_color !== null 
          ? ensureString(settings.completion_button_color) 
          : (prev.completionButtonColor || "#10b981"),
        completionButtonIcon: settings.completion_button_icon !== undefined && settings.completion_button_icon !== null 
          ? ensureString(settings.completion_button_icon) 
          : (prev.completionButtonIcon || "CheckCircle"),
        requirePostCompletionForm: settings.require_post_completion_form !== undefined 
          ? settings.require_post_completion_form 
          : (prev.requirePostCompletionForm || false),
        postCompletionFormId: settings.post_completion_form_id !== undefined && settings.post_completion_form_id !== null 
          ? ensureString(settings.post_completion_form_id) 
          : (prev.postCompletionFormId || ""),
        captureDeferDateInPostCompletionForm:
          settings.capture_defer_date_in_post_completion_form !== undefined
            ? Boolean(settings.capture_defer_date_in_post_completion_form)
            : prev.captureDeferDateInPostCompletionForm,
        postCompletionDeferDateFieldId:
          settings.post_completion_defer_date_field_id !== undefined &&
          settings.post_completion_defer_date_field_id !== null
            ? ensureString(settings.post_completion_defer_date_field_id)
            : prev.postCompletionDeferDateFieldId,
        allowMultipleInstances:
          settings.allow_multiple_instances !== undefined
            ? Boolean(settings.allow_multiple_instances)
            : prev.allowMultipleInstances,
        dynamicDateFormat: data.dynamicDateFormat ?? prev.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
      }
      
      return newData
    })
  }, [data.name, data.description, data.link, data.link_title, data.category, data.priority, data.due, data.status, data.deferrable, data.missable, data.taskTypeCategories, data.assignee_id, data.datePickerType, data.settings, data.dynamicDateFormat])

  useEffect(() => {
    const formId = taskData.postCompletionFormId
    if (!formId || !taskData.requirePostCompletionForm) {
      setPostCompletionFormDateFields([])
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`/forms/${formId}`, {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        })
        if (!res.ok || cancelled) return
        const payload = await res.json()
        const form = payload?.form
        const fields = Array.isArray(form?.fields) ? form.fields : []
        const dateFields = fields.filter((f) => f && f.type === "date")
        if (!cancelled) setPostCompletionFormDateFields(dateFields)
      } catch {
        if (!cancelled) setPostCompletionFormDateFields([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [
    taskData.postCompletionFormId,
    taskData.requirePostCompletionForm,
    postCompletionFormCanvasFingerprint,
  ])

  // Sync with workflow context when currentWorkflow changes
  useEffect(() => {
    if (currentWorkflow) {
      const nodeInWorkflow = currentWorkflow.nodes.find(node => node.id === id)
      if (nodeInWorkflow && nodeInWorkflow.data) {
        // Update local state to match workflow context
        const workflowData = nodeInWorkflow.data
        const settings = workflowData.settings || {}
        
        setTaskData(prev => {
          // Only update due date if it's actually different to prevent overriding user changes
          const shouldUpdateDue = workflowData.due !== undefined && workflowData.due !== prev.due;
          const formattedDue = shouldUpdateDue ? formatDateForInput(workflowData.due) : prev.due;
          
          const newData = {
            ...prev,
            name: workflowData.name !== undefined ? workflowData.name : prev.name,
            description: workflowData.description !== undefined ? workflowData.description : prev.description,
            link: workflowData.link !== undefined ? workflowData.link : prev.link,
            link_title: workflowData.link_title !== undefined ? workflowData.link_title : prev.link_title,
            category: workflowData.category || prev.category,
            priority: workflowData.priority || prev.priority,
            due: formattedDue,
            status: workflowData.status || prev.status,
            deferrable: workflowData.deferrable !== undefined ? workflowData.deferrable : prev.deferrable,
            missable: workflowData.missable !== undefined ? workflowData.missable : prev.missable,
            taskTypeCategories: workflowData.taskTypeCategories || prev.taskTypeCategories,
            assignee_id: workflowData.assignee_id !== undefined ? workflowData.assignee_id : prev.assignee_id,
            // Only update assignment if workflowData.assignment exists and is different from current
            assignment: workflowData.assignment && workflowData.assignment !== prev.assignment ? workflowData.assignment : prev.assignment,
            checklist: workflowData.checklist || prev.checklist,
            dynamicDueDate: workflowData.dynamicDueDate !== undefined ? workflowData.dynamicDueDate : prev.dynamicDueDate,
            showProbabilityScale: workflowData.showProbabilityScale !== undefined ? workflowData.showProbabilityScale : prev.showProbabilityScale,
            // Settings from workflow context
            completionButtonText: settings.completion_button_text !== undefined 
              ? settings.completion_button_text 
              : prev.completionButtonText,
            completionButtonColor: settings.completion_button_color !== undefined 
              ? settings.completion_button_color 
              : prev.completionButtonColor,
            completionButtonIcon: settings.completion_button_icon !== undefined 
              ? settings.completion_button_icon 
              : prev.completionButtonIcon,
            requirePostCompletionForm: settings.require_post_completion_form !== undefined 
              ? settings.require_post_completion_form 
              : prev.requirePostCompletionForm,
            postCompletionFormId: settings.post_completion_form_id !== undefined 
              ? settings.post_completion_form_id 
              : prev.postCompletionFormId,
            captureDeferDateInPostCompletionForm:
              settings.capture_defer_date_in_post_completion_form !== undefined
                ? Boolean(settings.capture_defer_date_in_post_completion_form)
                : prev.captureDeferDateInPostCompletionForm,
            postCompletionDeferDateFieldId:
              settings.post_completion_defer_date_field_id !== undefined
                ? ensureString(settings.post_completion_defer_date_field_id ?? "")
                : prev.postCompletionDeferDateFieldId,
            allowMultipleInstances:
              settings.allow_multiple_instances !== undefined
                ? Boolean(settings.allow_multiple_instances)
                : prev.allowMultipleInstances,
            dynamicDateFormat: workflowData.dynamicDateFormat ?? prev.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
          }
          
          return newData
        })
        
        // Update date picker type based on data
        // Only change if user hasn't manually set it
        if (!userSetDatePickerType.current) {
          const newTemplate = extractTemplateFromDue(workflowData.due)
          const hasTemplate = Boolean(newTemplate)
          
          // Determine date picker type based on data
          let newDatePickerType = "dynamic" // default
          
          if (workflowData.datePickerType) {
            newDatePickerType = workflowData.datePickerType
          } else if (hasTemplate) {
            newDatePickerType = "dynamic"
          } else if (workflowData.due && !isTemplateString(workflowData.due) && !containsExtractedVariables(workflowData.due)) {
            newDatePickerType = "calendar"
          }
          
          setDatePickerType(newDatePickerType)
        }
      }
    }
  }, [currentWorkflow, id])


  // Task type categories options
  const taskTypeCategoryOptions = [
    { value: "quality", label: "Quality", color: "#10b981" },
    { value: "speed", label: "Speed", color: "#3b82f6" },
    { value: "procedural", label: "Procedural", color: "#8b5cf6" },
  ]

  const toggleMinimize = () => {
    if (data.onNodeStateChange) {
      data.onNodeStateChange(id, !expanded)
    }
  }

  // Add effect to handle scroll containment within the node
  useEffect(() => {
    const handleWheel = (e) => {
      const nodeContent = e.target.closest('.node-content')
      
      if (nodeContent) {
        // Check if Ctrl key is pressed (for zoom functionality)
        if (e.ctrlKey) {
          // Allow default behavior for zoom
          return
        }

        // Check if Shift key is pressed (for global horizontal scrolling)
        if (e.shiftKey) {
          // Allow default behavior for global horizontal scrolling
          return
        }

        // Check if the node-content is scrollable and has scrollable content
        const isScrollable = nodeContent.scrollHeight > nodeContent.clientHeight
        const isAtTop = nodeContent.scrollTop === 0
        const isAtBottom = nodeContent.scrollTop + nodeContent.clientHeight >= nodeContent.scrollHeight - 1

        // Only stop propagation if we're scrolling within scrollable bounds
        if (isScrollable && !(isAtTop && e.deltaY < 0) && !(isAtBottom && e.deltaY > 0)) {
          e.stopPropagation()
        }
      }
    }

    // Add wheel event listener to capture scroll events
    document.addEventListener('wheel', handleWheel, { passive: false, capture: true })

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true })
    }
  }, [id, expanded])



  // Helper function to save node data to Builder (which triggers database save)
  const saveNodeDataToBuilder = useCallback((updatedData) => {
    if (data.onNodeDataChange) {
      // Extract settings fields and group them under settings key
      const {
        completionButtonText,
        completionButtonColor,
        completionButtonIcon,
        requirePostCompletionForm,
        postCompletionFormId,
        captureDeferDateInPostCompletionForm,
        postCompletionDeferDateFieldId,
        allowMultipleInstances,
        ...taskFields
      } = updatedData
      
      const nodeData = {
        ...taskFields,
        settings: {
          completion_button_text: completionButtonText,
          completion_button_color: completionButtonColor,
          completion_button_icon: completionButtonIcon,
          require_post_completion_form: requirePostCompletionForm,
          post_completion_form_id: postCompletionFormId,
          capture_defer_date_in_post_completion_form: Boolean(
            captureDeferDateInPostCompletionForm,
          ),
          post_completion_defer_date_field_id:
            postCompletionDeferDateFieldId &&
            String(postCompletionDeferDateFieldId).trim() !== ""
              ? String(postCompletionDeferDateFieldId).trim()
              : null,
          allow_multiple_instances: Boolean(allowMultipleInstances),
        }
      }
      
      data.onNodeDataChange(id, nodeData)
    }
  }, [data, id])

  // Helper function to update workflow context with task data
  const updateWorkflowContext = useCallback((updatedData) => {
    if (currentWorkflow) {
      // Extract settings fields and group them under settings key
      const {
        completionButtonText,
        completionButtonColor,
        completionButtonIcon,
        requirePostCompletionForm,
        postCompletionFormId,
        captureDeferDateInPostCompletionForm,
        postCompletionDeferDateFieldId,
        allowMultipleInstances,
        ...taskFields
      } = updatedData
      
      const nodeData = {
        ...taskFields,
        settings: {
          completion_button_text: completionButtonText,
          completion_button_color: completionButtonColor,
          completion_button_icon: completionButtonIcon,
          require_post_completion_form: requirePostCompletionForm,
          post_completion_form_id: postCompletionFormId,
          capture_defer_date_in_post_completion_form: Boolean(
            captureDeferDateInPostCompletionForm,
          ),
          post_completion_defer_date_field_id:
            postCompletionDeferDateFieldId &&
            String(postCompletionDeferDateFieldId).trim() !== ""
              ? String(postCompletionDeferDateFieldId).trim()
              : null,
          allow_multiple_instances: Boolean(allowMultipleInstances),
        }
      }
      
      // Update the workflow context - this updates both workflows array and currentWorkflow
      updateNode(currentWorkflow.id, id, { data: nodeData })
    }
  }, [currentWorkflow, id, updateNode])

  useEffect(() => {
    if (!taskData.requirePostCompletionForm) return
    if (String(taskData.postCompletionFormId || "").trim() !== "") return

    const connectedFormId = getConnectedPostCompletionFormId()
    if (!connectedFormId) return

    const updatedData = { ...taskData, postCompletionFormId: connectedFormId }
    setTaskData(updatedData)
    updateWorkflowContext(updatedData)
    saveNodeDataToBuilder(updatedData)
  }, [
    getConnectedPostCompletionFormId,
    saveNodeDataToBuilder,
    taskData,
    updateWorkflowContext,
  ])

  const handleChange = (field, value) => {
    let processedValue = value;
    
    // Special handling for due date to prevent timezone issues
    if (field === "due" && value) {
      // For manual date selection, ensure we treat it as a local date
      // and format it as YYYY-MM-DD to avoid timezone conversion issues
      if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // This is already in YYYY-MM-DD format from the date input
        processedValue = value;
      } else if (value instanceof Date) {
        // Convert Date object to YYYY-MM-DD format using UTC to avoid timezone issues
        const year = value.getUTCFullYear();
        const month = String(value.getUTCMonth() + 1).padStart(2, '0');
        const day = String(value.getUTCDate()).padStart(2, '0');
        processedValue = `${year}-${month}-${day}`;
      } else if (typeof value === 'string' && value.includes('T')) {
        // This is an ISO string, extract just the date part
        processedValue = value.split('T')[0];
      } else if (typeof value === 'string' && containsExtractedVariables(value)) {
        // This is a template string, keep it as is
        processedValue = value;
      } else if (typeof value === 'string' && isTemplateString(value)) {
        // This is a template string like ~today~, keep it as is
        processedValue = value;
      }
    }
    
    const updatedData = { ...taskData, [field]: processedValue }
    
    setTaskData(updatedData)
    
    // Update the workflow context directly to ensure data persists
    updateWorkflowContext(updatedData)
    
    // For due date changes, save immediately to database
    if (field === "due") {
      saveNodeDataToBuilder(updatedData)
    }
    // Note: Other fields will save on blur
  }

  // Handle blur events to save to database
  const handleBlur = (field) => {
    // Update the Builder's node data (this triggers the database save)
    saveNodeDataToBuilder(taskData)
  }

  const handleDateFormatChange = (format) => {
    setTaskData((prev) => {
      const updatedData = { ...prev, dynamicDateFormat: format }
      updateWorkflowContext(updatedData)
      saveNodeDataToBuilder(updatedData)
      return updatedData
    })
  }

  // Handle task type category selection
  const handleTaskTypeCategoryToggle = (categoryValue) => {
    const currentCategories = taskData.taskTypeCategories || []
    const isSelected = currentCategories.includes(categoryValue)

    let newCategories
    if (isSelected) {
      newCategories = currentCategories.filter((cat) => cat !== categoryValue)
    } else {
      newCategories = [...currentCategories, categoryValue]
    }

    // Create the updated data first
    const updatedData = { ...taskData, taskTypeCategories: newCategories }
    
    // Update state and workflow context
    handleChange("taskTypeCategories", newCategories)
    
    // Save to database with the updated data
    saveNodeDataToBuilder(updatedData)
  }

  // Remove a task type category
  const removeTaskTypeCategory = (categoryValue) => {
    const currentCategories = taskData.taskTypeCategories || []
    const newCategories = currentCategories.filter((cat) => cat !== categoryValue)
    
    // Create the updated data first
    const updatedData = { ...taskData, taskTypeCategories: newCategories }
    
    // Update state and workflow context
    handleChange("taskTypeCategories", newCategories)
    
    // Save to database with the updated data
    saveNodeDataToBuilder(updatedData)
  }

  const getPriorityColor = (priority) => {
    const priorityNum = Number.parseInt(priority, 10)
    if (priorityNum >= 8) return "text-red-500"
    if (priorityNum >= 5) return "text-orange-500"
    return "text-blue-500"
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "To Do":
        return "bg-gray-200 text-gray-800"
      case "In Progress":
        return "bg-blue-200 text-blue-800"
      case "In Review":
        return "bg-purple-200 text-purple-800"
      case "Completed":
        return "bg-green-200 text-green-800"
      default:
        return "bg-gray-200 text-gray-800"
    }
  }

  // Dynamic date options for business settings
  const dynamicDateOptions = [
    { value: "today", label: "Day of Task Creation", icon: <Calendar size={14} /> },
    { value: "tomorrow", label: "Next Day After Task Creation", icon: <Calendar size={14} /> },
    { value: "next_business_day", label: "Next Business Day After Task Creation", icon: <Calendar size={14} /> },
    { value: "end_of_week", label: "End of Week (Friday) of Task Creation", icon: <Calendar size={14} /> },
    { value: "end_of_month", label: "End of Month of Task Creation", icon: <Calendar size={14} /> },
    { value: "end_of_quarter", label: "End of Quarter of Task Creation", icon: <Calendar size={14} /> },
    { value: "1_week", label: "1 Week After Task Creation", icon: <Clock size={14} /> },
    { value: "2_weeks", label: "2 Weeks After Task Creation", icon: <Clock size={14} /> },
    { value: "30_days", label: "30 Days After Task Creation", icon: <Clock size={14} /> },
    { value: "60_days", label: "60 Days After Task Creation", icon: <Clock size={14} /> },
    { value: "90_days", label: "90 Days After Task Creation", icon: <Clock size={14} /> },
    { value: "custom", label: "Custom Period...", icon: <Clock size={14} /> },
  ]  

  // Handle dynamic date selection
  const handleDynamicDateChange = (option) => {
    setDynamicDateOption(option)

    if (option === "custom") {
      setShowCustomInput(true)
    } else {
      setShowCustomInput(false)
      
      // Store the template string instead of calculating the actual date
      const templateString = `~${option}~`
      
      // Update both fields at once to avoid multiple saves
      const updatedData = { ...taskData, dynamicDueDate: option, due: templateString }
      setTaskData(updatedData)
      
      // Update workflow context
      updateWorkflowContext(updatedData)
      
      // Save to backend using the normal save mechanism
      saveNodeDataToBuilder(updatedData)
    }
  }

  // Handle custom period change
  const handleCustomPeriodChange = () => {
    // Format template based on period type
    const dynamicValue = customPeriod === "day_of_month" 
      ? `custom_day_of_month_${customDays}`
      : `custom_${customDays}_${customPeriod}`
    
    // Store the template string instead of calculating the actual date
    const templateString = `~${dynamicValue}~`
    
    // Update both fields at once to avoid multiple saves
    const updatedData = { ...taskData, dynamicDueDate: dynamicValue, due: templateString }
    setTaskData(updatedData)
    
    // Update workflow context
    updateWorkflowContext(updatedData)
    
    // Save to backend using the normal save mechanism
    saveNodeDataToBuilder(updatedData)
  }

  // Handle switching between calendar and dynamic date picker
  const handleDatePickerTypeChange = (type) => {
    userSetDatePickerType.current = true
    setDatePickerType(type)
    
    if (type === 'calendar') {
      // Clear dynamic date settings when switching to calendar
      setDynamicDateOption('')
      setShowCustomInput(false)
      
      // If current due date is a template string or contains variables, clear it
      if (isTemplateString(taskData.due) || containsExtractedVariables(taskData.due)) {
        const updatedData = { ...taskData, due: '', dynamicDueDate: '' }
        setTaskData(updatedData)
        
        // Update workflow context
        updateWorkflowContext(updatedData)
        
        // Save to database
        saveNodeDataToBuilder(updatedData)
      }
    } else if (type === 'dynamic') {
      // When switching to dynamic, set default to "today" (Day of Task Creation)
      setDynamicDateOption('today')
      setShowCustomInput(false)
      
      // If current due date is not a template string, set it to today's template
      if (!isTemplateString(taskData.due) && !containsExtractedVariables(taskData.due)) {
        const updatedData = { 
          ...taskData, 
          due: '~today~', 
          dynamicDueDate: 'today',
          datePickerType: type
        }
        setTaskData(updatedData)
        
        // Update workflow context
        updateWorkflowContext(updatedData)
        
        // Save to database
        saveNodeDataToBuilder(updatedData)
        return // Exit early since we already saved
      }
    }
    
    // Always save the date picker type change to the database
    const updatedData = { ...taskData, datePickerType: type }
    setTaskData(updatedData)
    updateWorkflowContext(updatedData)
    saveNodeDataToBuilder(updatedData)
  }

  // Calculate dynamic date based on option
  const calculateDynamicDate = (option) => {
    const today = new Date()
    let resultDate = new Date(today)

    switch (option) {
      case "today":
        break
      case "tomorrow":
        resultDate.setDate(today.getDate() + 1)
        break
      case "next_business_day":
        // Skip to Monday if today is Friday, Saturday, or Sunday
        const dayOfWeek = today.getDay() // 0 = Sunday, 6 = Saturday
        if (dayOfWeek === 5)
          resultDate.setDate(today.getDate() + 3) // Friday -> Monday
        else if (dayOfWeek === 6)
          resultDate.setDate(today.getDate() + 2) // Saturday -> Monday
        else if (dayOfWeek === 0)
          resultDate.setDate(today.getDate() + 1) // Sunday -> Monday
        else resultDate.setDate(today.getDate() + 1) // Otherwise just tomorrow
        break
      case "end_of_week":
        // Set to this Friday (or next Friday if today is Friday)
        const daysUntilFriday = (5 - today.getDay() + 7) % 7
        resultDate.setDate(today.getDate() + (daysUntilFriday === 0 ? 7 : daysUntilFriday))
        break
      case "end_of_month":
        // Set to last day of current month
        resultDate = new Date(today.getFullYear(), today.getMonth() + 1, 0)
        break
      case "end_of_quarter":
        // Set to last day of current quarter
        const currentMonth = today.getMonth()
        const quarterEndMonth = Math.floor(currentMonth / 3) * 3 + 2 // 0->2, 1->2, 2->2, 3->5, etc.
        resultDate = new Date(today.getFullYear(), quarterEndMonth + 1, 0)
        break
      case "1_week":
        resultDate.setDate(today.getDate() + 7)
        break
      case "2_weeks":
        resultDate.setDate(today.getDate() + 14)
        break
      case "30_days":
        resultDate.setDate(today.getDate() + 30)
        break
      case "60_days":
        resultDate.setDate(today.getDate() + 60)
        break
      case "90_days":
        resultDate.setDate(today.getDate() + 90)
        break
      default:
        break
    }

    return resultDate.toISOString().split("T")[0] // Format as YYYY-MM-DD
  }

  // Calculate custom date
  const calculateCustomDate = (amount, period) => {
    const today = new Date()
    const resultDate = new Date(today)

    switch (period) {
      case "days":
        resultDate.setDate(today.getDate() + Number.parseInt(amount))
        break
      case "weeks":
        resultDate.setDate(today.getDate() + Number.parseInt(amount) * 7)
        break
      case "months":
        resultDate.setMonth(today.getMonth() + Number.parseInt(amount))
        break
      case "quarters":
        resultDate.setMonth(today.getMonth() + Number.parseInt(amount) * 3)
        break
      default:
        break
    }

    return resultDate.toISOString().split("T")[0] // Format as YYYY-MM-DD
  }

  // Get human-readable date description
  const getDynamicDateDescription = (option) => {
    if (!option) return ""
    // Check if the option contains variables
    if (containsExtractedVariables(option)) {
      return "Dynamic date with extracted variables"
    }

    if (option.startsWith("custom_day_of_month_")) {
      const day = option.replace("custom_day_of_month_", "")
      return `Day ${day} of month`
    }

    if (option.startsWith("custom_")) {
      const parsedTemplate = parseCustomTemplate(option)
      if (parsedTemplate) {
        const periodLabel = formatCustomPeriodLabel(parsedTemplate.period, parsedTemplate.amount)
        return `${parsedTemplate.amount} ${periodLabel} from now`
      }
      return "Custom period"
    }

    const found = dynamicDateOptions.find((opt) => opt.value === option)
    return found ? found.label : ""
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return ""

    // Handle template strings
    if (isTemplateString(dateString)) {
      // Check if template contains variables
      if (containsExtractedVariables(dateString)) {
        return
      }

      const template = dateString.slice(1, -1) // Remove ~ characters
      
      // Handle day of month templates
      if (template.startsWith('custom_day_of_month_')) {
        const day = template.replace('custom_day_of_month_', '')
        return `Day ${day} of month (dynamic)`
      }
      
      // Handle custom templates
      if (template.startsWith('custom_')) {
        const parsedTemplate = parseCustomTemplate(template)
        if (parsedTemplate) {
          const periodLabel = formatCustomPeriodLabel(parsedTemplate.period, parsedTemplate.amount)
          return `${parsedTemplate.amount} ${periodLabel} from now (dynamic)`
        }
        return "Custom period (dynamic)"
      }
      
      // Handle standard templates
      const found = dynamicDateOptions.find((opt) => opt.value === template)
      return found ? `${found.label} (dynamic)` : `${template} (dynamic)`
    }

    // Handle actual dates
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return dateString // Return as-is if not a valid date
    }
    
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  // Add these styles to match other select elements
  const selectStyles = {
    dynamicDateSelect: {
      toggle: {
        width: "100%",
        padding: "6px 8px",
        border: "1px solid #ddd",
        borderRadius: "4px",
        fontSize: "13px",
        backgroundColor: "white",
        textAlign: "left",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: "pointer",
      },
      menu: {
        position: "absolute",
        width: "100%",
        maxHeight: "250px", // Increased from 200px
        overflowY: "auto",
        backgroundColor: "white",
        border: "1px solid #ddd",
        borderRadius: "4px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        zIndex: 100,
        marginTop: "2px",
      },
    },
  }

  // Function to get assignee name and email
  const getAssigneeInfo = (assigneeId) => {
    if (!assigneeId || !users) return { name: "", email: "" }
    
    // Convert assigneeId to string for comparison since IDs from database are bigints
    const assignee = users.find(user => user.id.toString() === assigneeId.toString())
    return assignee ? {
      name: `${assignee.firstName} ${assignee.lastName}`,
      email: assignee.email
    } : { name: "", email: "" }
  }

  // Set default assignee to current user when creating a new task
  useEffect(() => {
    // Only set default assignee if there's no assignee_id in both data and taskData
    if (!data.assignee_id && !taskData.assignee_id && users?.length > 0) {
      const currentUser = users.find(user => user.isCurrentUser)
      if (currentUser) {
        handleChange("assignee_id", currentUser.id.toString())
      }
    }
  }, [users, data.assignee_id])

  const hasError = hasFormValidationError()
  
  return (
    <div
      className={`trigger-node-container dark:bg-card ${hasError ? "task-node-error-outline" : "task-node-outline"} ${!expanded ? "minimized" : ""} ${isHovering ? (hasError ? "task-node-error-hovering" : "task-node-hovering") : ""}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* <NodeControls id={id} /> */}

      {/* Node Header - This part should be draggable */}
      <div className="trigger-node-header dark:bg-card dark:border-border">
        <div className="trigger-node-title">
          <CheckSquare size={16} className="trigger-icon" />
          <span>{taskData.name && taskData.name.trim() !== "" ? taskData.name : "New Task"}</span>
          {hasError && (
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "4px", 
              color: "#dc2626", 
              fontSize: "11px",
              fontWeight: "normal"
            }}>
              <AlertTriangle size={10} />
              <span>Missing Form</span>
            </div>
            )}
        </div>
        <div className="trigger-node-controls">
          <CopyPasteButtons 
            nodeId={id}
            nodeType="taskNode"
            nodeName={taskData.name}
            className="nodrag"
          />
          <div className="minimize-button-container nodrag">
            <button
              className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
              onClick={(e) => {
                e.stopPropagation()
                toggleMinimize()
              }}
              onMouseEnter={() => setShowMinimizeTooltip(true)}
              onMouseLeave={() => setShowMinimizeTooltip(false)}
            >
              {!expanded ? <Plus size={16} /> : <Minus size={16} />}
            </button>
            {showMinimizeTooltip && <div className="minimize-tooltip"></div>}
          </div>
          {/* <div className="square-button-container nodrag">
            <button
              className="trigger-node-button nodrag"
              onMouseEnter={() => setShowSquareTooltip(true)}
              onMouseLeave={() => setShowSquareTooltip(false)}
              // onClick={handleOpenInTab}
            >
              <Table2 size={16} />
            </button>
            {showSquareTooltip && <div className="square-tooltip">Open in tab</div>}
          </div> */}
          {/* <button className="trigger-node-button nodrag">
            <MoreHorizontal size={16} />
          </button> */}
          <button 
            className="trigger-node-button nodrag dark:hover:bg-card"
            onClick={async (e) => {
              e.stopPropagation()
              if (confirm('Are you sure you want to delete this node?')) {
                try {
                  if (!resolvedWorkflowId || !stepDatabaseId) {
                    throw new Error('Unable to delete node: missing workflow or step database ID')
                  }
                  router.delete(`/workflows/${resolvedWorkflowId}/steps/${stepDatabaseId}`, {
                    preserveState: true,
                    preserveScroll: true,
                    only: [],
                    onSuccess: () => {
                      if (typeof data.onDeleteNode === "function") {
                        data.onDeleteNode(id)
                      }
                    },
                    onError: (errors) => {
                      console.error('Failed to delete task node via Inertia:', errors)
                      alert('Failed to delete node. Please try again.')
                    },
                  })
                  // Fallback path retained for legacy contexts without onDelete callback.
                  if (typeof data.onDeleteNode !== "function" && deleteNode && currentWorkflow) {
                    await deleteNode(currentWorkflow.id, id, csrf_token)
                  } else {
                    // no-op: Inertia success + onDeleteNode handles visual removal
                  }
                } catch (error) {
                  console.error('Failed to delete node:', error)
                  alert('Failed to delete node. Please try again.')
                }
              }
            }}
            title="Delete node"
            style={{ color: '#ef4444' }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="node-content">
          {/* Tabs */}
          <div className="task-tabs dark:border-none">
            <button
              className={`tab-button dark:bg-[#1f2937] dark:text-white ${activeTab === "task" ? "active" : ""}`}
              onClick={() => setActiveTab("task")}
            >
              Task Details
            </button>
            <button
              className={`tab-button dark:bg-[#1f2937] dark:text-white ${activeTab === "checklist" ? "active" : ""}`}
              onClick={() => setActiveTab("checklist")}
            >
              Checklist
            </button>
            <button
              className={`tab-button dark:bg-[#1f2937] dark:text-white ${activeTab === "settings" ? "active" : ""}`}
              onClick={() => setActiveTab("settings")}
            >
              Settings
            </button>
          </div>

          {/* Task Details Tab */}
          {activeTab === "task" && (
            <div className="task-tab-content">
              <div className="node-field">
                <label className="dark:text-white">Task Type</label>
                <div className="date-picker-tabs dark:bg-[#1f2937]">
                  <button
                    className={`date-picker-tab ${taskType === "standard" ? "active dark:bg-[#3B82F6] dark:text-white" : ""}`}
                    onClick={() => {
                      // Create the updated data first
                      const updatedData = { ...taskData, taskType: "standard" }
                      
                      // Update state
                      setTaskType("standard")
                      
                      // Save to database with the updated data
                      saveNodeDataToBuilder(updatedData)
                    }}
                  >
                    <CheckSquare size={14} />
                    <span>Standard</span>
                  </button>
                  <button
                    className={`date-picker-tab ${taskType === "sales" ? "active dark:bg-[#3B82F6] dark:text-white" : ""}`}
                    onClick={() => {
                      // Create the updated data first
                      const updatedData = { ...taskData, taskType: "sales" }
                      
                      // Update state
                      setTaskType("sales")
                      
                      // Save to database with the updated data
                      saveNodeDataToBuilder(updatedData)
                    }}
                  >
                    <DollarSign size={14} />
                    <span>Sales</span>
                  </button>
                </div>
              </div>

              <div className="node-field">
                <label className="dark:text-white" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  Task Name
                </label>
                <EnhancedVariableReference
                  value={taskData.name}
                  onChange={(value) => handleChange("name", value)}
                  onBlur={() => handleBlur("name")}
                  availableVariables={getAllAvailableVariables()}
                  systemVariables={systemVariables}
                  placeholder="Enter task name or use variables..."
                  className="w-full"
                  showDateVariables={true}
                  showPreview={true}
                  dateFormat={taskData.dynamicDateFormat}
                  onDateFormatChange={handleDateFormatChange}
                />
                {/* Show connected request nodes */}
                {(() => {
                  const connectedRequests = getConnectedRequestNodes()
                  if (connectedRequests.length > 0) {
                    return (
                      <div style={{ 
                        marginTop: '4px', 
                        fontSize: '11px', 
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Globe size={10} />
                        <span>Connected to: {connectedRequests.map(req => req.name).join(', ')}</span>
                      </div>
                    )
                  }
                  return null
                })()}
                
                {/* Show available form fields indicator */}
                {(() => {
                  const formFields = getAvailableFormFields()
                  if (formFields.length > 0) {
                    return (
                      <div style={{ 
                        marginTop: '4px', 
                        fontSize: '11px', 
                        color: '#059669',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <FileText size={10} />
                        <span>Available form fields: {formFields.length} field{formFields.length !== 1 ? 's' : ''}</span>
                      </div>
                    )
                  }
                  return null
                })()}
              </div>

              <div className="node-field">
                <label className="dark:text-white">Task Type Categories</label>
                <div className="task-type-categories-container">
                  {/* Dropdown for selecting categories */}
                  <div className="category-dropdown" style={{ position: "relative" }}>
                    <button
                    className="category-select-toggle w-full px-[6px] py-[8px] border border-gray-300 rounded text-sm bg-white text-left flex justify-between items-center cursor-pointer min-h-8 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none"
                      onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1, overflow: "hidden" }}>
                        {taskData.taskTypeCategories && taskData.taskTypeCategories.length > 0 ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "4px", flex: 1, overflow: "hidden" }}>
                            {taskData.taskTypeCategories.map((categoryValue) => {
                              const category = taskTypeCategoryOptions.find((opt) => opt.value === categoryValue)
                              return (
                                <span
                                  key={categoryValue}
                                  className="category-tag"
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "3px",
                                    padding: "2px 6px",
                                    backgroundColor: category?.color || "#6b7280",
                                    color: "white",
                                    borderRadius: "10px",
                                    fontSize: "11px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {category?.label || categoryValue}
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      removeTaskTypeCategory(categoryValue)
                                    }}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      color: "white",
                                      cursor: "pointer",
                                      padding: "0",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <X size={10} />
                                  </span>
                                </span>
                              )
                            })}
                          </div>
                        ) : (
                          <span style={{ color: "#9ca3af" }}>Select task type categories...</span>
                        )}
                      </div>
                      <ChevronDown
                        size={16}
                        className={`dropdown-arrow ${isCategoryDropdownOpen ? "open" : ""}`}
                        style={{ flexShrink: 0, marginLeft: "8px" }}
                      />
                    </button>

                    {isCategoryDropdownOpen && (
                      <div
                        className="category-select-menu nodrag absolute top-full left-0 right-0 w-full bg-white border border-gray-300 rounded shadow-lg z-50 mt-0.5 dark:bg-gray-800 dark:border-gray-600"
                        onClick={(e) => e.stopPropagation()}
                        onWheel={(e) => e.stopPropagation()}
                      >
                        {/* Available options section */}
                        <div className="py-1">
                          {taskTypeCategoryOptions.map((option) => {
                            const isSelected = taskData.taskTypeCategories?.includes(option.value) || false
                            return (
                              <button
                                key={option.value}
                                className={`category-option w-full px-3 py-2 border-none text-left cursor-pointer flex items-center gap-2 text-sm transition-colors duration-150 ${
                                  isSelected 
                                    ? 'bg-gray-100 dark:bg-gray-700' 
                                    : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                                onClick={() => handleTaskTypeCategoryToggle(option.value)}
                              >
                                <div
                                  className="w-3 h-3 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: option.color }}
                                />
                                <span className="dark:text-white">{option.label}</span>
                                {isSelected && (
                                  <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                                    ✓
                                  </span>
                                )}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="node-field">
                <label className="dark:text-white">Description</label>
                <EnhancedVariableReference
                  value={taskData.description}
                  onChange={(value) => handleChange("description", value)}
                  onBlur={() => handleBlur("description")}
                  availableVariables={getAllAvailableVariables()}
                  systemVariables={systemVariables}
                  placeholder="Enter task description or use variables..."
                  className="w-full"
                  multiline={true}
                  rows={3}
                  showDateVariables={true}
                  showPreview={true}
                  dateFormat={taskData.dynamicDateFormat}
                  onDateFormatChange={handleDateFormatChange}
                />
              </div>

              <div className="node-field">
                <label className="dark:text-white">Link</label>
                <EnhancedVariableReference
                  value={taskData.link}
                  onChange={(value) => handleChange("link", value)}
                  onBlur={() => handleBlur("link")}
                  availableVariables={getAllAvailableVariables()}
                  systemVariables={systemVariables}
                  placeholder="Enter task link or use variables..."
                  className="w-full"
                  showDateVariables={true}
                  showPreview={true}
                  dateFormat={taskData.dynamicDateFormat}
                  onDateFormatChange={handleDateFormatChange}
                />
              </div>
              <div className="node-field">
                <label className="dark:text-white">Link Title</label>
                <EnhancedVariableReference
                  value={taskData.link_title}
                  onChange={(value) => handleChange("link_title", value)}
                  onBlur={() => handleBlur("link_title")}
                  availableVariables={getAllAvailableVariables()}
                  systemVariables={systemVariables}
                  placeholder="Enter link title (optional)"
                  className="w-full"
                  showDateVariables={true}
                  showPreview={true}
                  dateFormat={taskData.dynamicDateFormat}
                  onDateFormatChange={handleDateFormatChange}
                />
              </div>

              <div className="node-field-row">
                <div className="node-field">
                  <label className="dark:text-white">Category</label>
                  <div className="flex items-center">
                    <select 
                      value={taskData.category} 
                      onChange={(e) => handleChange("category", e.target.value)}
                      onBlur={() => handleBlur("category")}
                      className="w-full dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                    >
                      {categories.map((category) => (
                        <option key={category.id} value={category.name}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="node-field">
                  <label className="dark:text-white">Priority</label>
                  <div className="flex items-center">
                    <select className="w-full dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none" value={taskData.priority} onChange={(e) => handleChange("priority", e.target.value)} onBlur={() => handleBlur("priority")}>
                      <option value="1">1 (Lowest)</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="6">6</option>
                      <option value="7">7</option>
                      <option value="8">8</option>
                      <option value="9">9</option>
                      <option value="10">10 (Highest)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="node-field">
                <label className="dark:text-white">Status</label>
                <div className="flex items-center">
                  <select className="w-full dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none" value={taskData.status} onChange={(e) => handleChange("status", e.target.value)} onBlur={() => handleBlur("status")}>
                    <option value="To Do">To Do</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
              </div>

              <div className="node-field">
                <label className="dark:text-white">Due Date</label>
                <div className="date-picker-tabs dark:bg-[#1f2937]">
                  <button
                    className={`date-picker-tab ${datePickerType === "calendar" ? "active dark:bg-[#3B82F6] dark:text-white" : ""}`}
                    onClick={() => handleDatePickerTypeChange("calendar")}
                  >
                    <Calendar size={14} />
                    <span>Calendar</span>
                  </button>
                  <button
                    className={`date-picker-tab ${datePickerType === "dynamic" ? "active dark:bg-[#3B82F6] dark:text-white" : ""}`}
                    onClick={() => handleDatePickerTypeChange("dynamic")}
                  >
                    <Clock size={14} />
                    <span>Dynamic</span>
                  </button>
                </div>

                {datePickerType === "calendar" ? (
                  <input
                    type="date"
                    value={taskData.due}
                    onChange={(e) => handleChange("due", e.target.value)}
                    onBlur={() => handleBlur("due")}
                    className="w-full cursor-pointer px-2 py-1.5 border border-gray-300 rounded text-sm dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500"
                    min={formatDateForInput(new Date().toISOString().split('T')[0])}
                  />
                ) : (
                  <div>
                    <div
                      className="mb-3 nodrag"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-300 block mb-1">
                        Date output format
                      </label>
                      <select
                        value={normalizeDynamicDateFormatPreset(taskData.dynamicDateFormat)}
                        onChange={(e) => handleDateFormatChange(e.target.value)}
                        className="w-full text-sm px-2 py-1.5 rounded-md border border-gray-300 bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 nodrag focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {DYNAMIC_DATE_FORMAT_PRESETS.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-snug">
                        Used for ~date~ tokens in this task, previews, and workflow execution.
                      </p>
                    </div>

                    <DynamicDateSelector
                      value={dynamicDateOption}
                      onChange={handleDynamicDateChange}
                      onCustomPeriodChange={handleCustomPeriodChange}
                      customDays={customDays}
                      setCustomDays={setCustomDays}
                      customPeriod={customPeriod}
                      setCustomPeriod={setCustomPeriod}
                      showCustomInput={showCustomInput}
                      setShowCustomInput={setShowCustomInput}
                    />

                    {/* Variable Reference for Dynamic Due Date */}
                    <div style={{ marginTop: "8px" }}>
                      <label style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px", display: "block" }} className="dark:text-white">
                        Add date variables:
                      </label>
                      <EnhancedVariableReference
                        value={taskData.due || ""}
                        onChange={(value) => {
                          // Update the due field directly
                          handleChange("due", value)
                          const updatedData = { ...taskData, due: value, }
                          // Save to database with the updated data
                          saveNodeDataToBuilder(updatedData)
                          }}
                        availableVariables={getAllAvailableVariables().filter(variable => 
                          variable && variable.name && typeof variable.name === 'string' && (
                            variable.name.toLowerCase().includes('date') || 
                            variable.name.toLowerCase().includes('time') ||
                            variable.name.toLowerCase().includes('due') ||
                            variable.name.toLowerCase().includes('created') ||
                            variable.name.toLowerCase().includes('updated') ||
                            variable.name.toLowerCase().includes('deadline') ||
                            variable.name.toLowerCase().includes('expiry') ||
                            variable.name.toLowerCase().includes('start') ||
                            variable.name.toLowerCase().includes('end') ||
                            variable.name.toLowerCase().includes('finish') ||
                            variable.name.toLowerCase().includes('complete')
                          )
                        )}
                        placeholder="Add date variables to dynamic due date..."
                        className="w-full"
                        showDateVariables={true}
                        showSystemVariables={false}
                        showPreview={true}
                        dateFormat={taskData.dynamicDateFormat}
                        onDateFormatChange={handleDateFormatChange}
                      />
                    </div>
                  </div>
                )}
              </div>

              {taskType === "sales" && (
                <div className="node-field" style={{ marginTop: 8 }}>
                  <label className="text-[#374151] dark:text-white" style={{ display: "flex", alignItems: "center", fontWeight: 400, fontSize: 15 }}>
                    <input
                      type="checkbox"
                      checked={!!taskData.showProbabilityScale}
                      onChange={(e) => {
                        // Create the updated data first
                        const updatedData = { ...taskData, showProbabilityScale: e.target.checked ? 1 : 0 }
                        
                        // Update state and workflow context
                        handleChange("showProbabilityScale", e.target.checked ? 1 : 0)
                        
                        // Save to database with the updated data
                        saveNodeDataToBuilder(updatedData)
                      }}
                      style={{ marginRight: 8, width: 16, height: 16 }}
                    />
                    Show probability of closing sale
                  </label>
                </div>
              )}

              <div className="node-field" style={{ marginTop: 8 }}>
                <label className="text-[#374151] dark:text-white" style={{ display: "flex", alignItems: "center", fontWeight: 400, fontSize: 15 }}>
                  <input
                    type="checkbox"
                    checked={!!taskData.deferrable}
                    onChange={(e) => {
                      // Create the updated data first
                      const updatedData = { ...taskData, deferrable: e.target.checked ? 1 : 0 }
                      
                      // Update state and workflow context
                      handleChange("deferrable", e.target.checked ? 1 : 0)
                      
                      // Save to database with the updated data
                      saveNodeDataToBuilder(updatedData)
                    }}
                    style={{ marginRight: 8, width: 16, height: 16 }}
                  />
                  This task can be deferred if needed
                </label>
              </div>

              <div className="node-field" style={{ marginTop: 8 }}>
                <label className="text-[#374151] dark:text-white" style={{ display: "flex", alignItems: "center", fontWeight: 400, fontSize: 15 }}>
                  <input
                    type="checkbox"
                    checked={!!taskData.missable}
                    onChange={(e) => {
                      // Create the updated data first
                      const updatedData = { ...taskData, missable: e.target.checked ? 1 : 0 }
                      // Update state and workflow context
                      handleChange("missable", e.target.checked ? 1 : 0)
                      
                      // Save to database with the updated data
                      saveNodeDataToBuilder(updatedData)
                    }}
                    style={{ marginRight: 8, width: 16, height: 16 }}
                  />
                  This task can be missed if overdue
                </label>
              </div>

              {/* Assignment Selector */}
              <div className="node-field">
                <label className="dark:text-white">Assignment</label>
                <AssignmentSelector
                  value={taskData.assignment}
                  onChange={(assignment) => {
                    const updatedData = { ...taskData, assignment }
                    setTaskData(updatedData)
                    updateWorkflowContext(updatedData)
                    saveNodeDataToBuilder(updatedData)
                  }}
                  users={users}
                  departments={departments}
                  integrations={integrations}
                  availableVariables={getAvailableVariables()}
                  workflowAssignment={currentWorkflowData?.assignment_type ? {
                    type: currentWorkflowData.assignment_type,
                    user_ids: currentWorkflowData.assigned_user_ids || [],
                    department_ids: currentWorkflowData.assigned_department_ids || [],
                    shared: true
                  } : null}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Choose how this task should be assigned when the workflow runs
                </p>
              </div>
            </div>
          )}

          {/* Checklist Tab */}
          {activeTab === "checklist" && (
            <div className="checklist-tab-content">
              <div className="node-field">
                <label>Checklist Items</label>
                <p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "12px" }}>
                  Add checklist items that will be transferred to tasks when this workflow runs. 
                  Tasks cannot be completed until all checklist items are checked.
                </p>
                <Checklist
                  items={taskData.checklist}
                  onUpdate={(updatedChecklist) => {
                    // Create the updated data first
                    const updatedData = { ...taskData, checklist: updatedChecklist }
                    
                    // Update state and workflow context
                    handleChange("checklist", updatedChecklist)
                    
                    // Save to database with the updated data
                    saveNodeDataToBuilder(updatedData)
                  }}
                  isCollapsible={false}
                  showProgress={false}
                  maxHeight="300px"
                  isCheckable={false}
                  onInteraction={onChecklistInteraction}
                  allowDrag={false}
                  availableVariables={getAvailableVariables()}
                />
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <div className="settings-tab-content">
              <div className="node-field">
                <label className="dark:text-white">Completion Button Text</label>
                <EnhancedVariableReference
                  value={taskData.completionButtonText}
                  onChange={(value) => handleChange("completionButtonText", value)}
                  onBlur={() => handleBlur("completionButtonText")}
                  availableVariables={getAvailableVariables()}
                  systemVariables={systemVariables}
                  placeholder="Complete Task"
                  className="w-full"
                  showDateVariables={true}
                  showPreview={true}
                  dateFormat={taskData.dynamicDateFormat}
                  onDateFormatChange={handleDateFormatChange}
                />
              </div>

              <div className="node-field">
                <label className="dark:text-white">Completion Button Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={taskData.completionButtonColor}
                    onChange={(e) => handleChange("completionButtonColor", e.target.value)}
                    onBlur={() => handleBlur("completionButtonColor")}
                    style={{ width: "40px", height: "32px", borderRadius: "4px" }}
                    className="border border-gray-200 dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                  />
                  <input
                    type="text"
                    value={taskData.completionButtonColor}
                    onChange={(e) => handleChange("completionButtonColor", e.target.value)}
                    onBlur={() => handleBlur("completionButtonColor")}
                    placeholder="#10b981"
                    className="flex-1 border border-gray-200 dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                    style={{ padding: "6px 8px", borderRadius: "4px", fontSize: "13px" }}
                  />
                </div>
              </div>

              <div className="node-field">
                <label className="dark:text-white">Completion Button Icon</label>
                <div className="grid grid-cols-4 gap-2 p-3 border rounded-md bg-gray-50 dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none">
                  {[
                    { name: "CheckCircle", icon: CheckCircle },
                    { name: "Check", icon: Check },
                    { name: "CheckCheck", icon: CheckCheck },
                    { name: "Star", icon: Star },
                    { name: "Trophy", icon: Trophy },
                    { name: "Award", icon: Award },
                    { name: "Zap", icon: Zap },
                    { name: "Target", icon: Target },
                    { name: "Flag", icon: Flag },
                    { name: "Heart", icon: Heart },
                    { name: "ThumbsUp", icon: ThumbsUp },
                    { name: "Smile", icon: Smile },
                    { name: "PartyPopper", icon: PartyPopper },
                    { name: "ArrowBigRight", icon: ArrowBigRight },
                  ].map(({ name, icon: IconComponent }) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => {
                        // Create the updated data first
                        const updatedData = { ...taskData, completionButtonIcon: name }
                        
                        // Update state and workflow context
                        handleChange("completionButtonIcon", name)
                        
                        // Save to database with the updated data
                        saveNodeDataToBuilder(updatedData)
                      }}
                      className={`p-2 rounded-md border-2 flex items-center justify-center transition-all hover:scale-105 ${
                        taskData.completionButtonIcon === name
                          ? "border-blue-500 bg-blue-50 shadow-md dark:bg-card dark:text-white"
                          : "border-gray-200 bg-white hover:border-gray-300 dark:bg-card dark:text-white dark:border-none dark:focus:outline-none"
                      }`}
                      title={name}
                    >
                      <IconComponent 
                        size={20} 
                        className={`${
                          taskData.completionButtonIcon === name
                            ? "text-blue-600"
                            : "text-gray-600"
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: "12px", marginTop: "4px" }} className="text-gray-500 dark:text-blue-600">
                  Selected: {taskData.completionButtonIcon}
                </p>
              </div>

              <div className="node-field">
                <label style={{ display: "flex", alignItems: "center", fontWeight: 400, fontSize: 15 }} className="text-gray-500 dark:text-white">
                  <input
                    type="checkbox"
                    checked={!!taskData.requirePostCompletionForm}
                    onChange={(e) => {
                      // Create the updated data first
                      const updatedData = { ...taskData, requirePostCompletionForm: e.target.checked }
                      
                      // Update state and workflow context
                      handleChange("requirePostCompletionForm", e.target.checked)
                      
                      // Save to database with the updated data
                      saveNodeDataToBuilder(updatedData)
                    }}
                    style={{ marginRight: 8, width: 16, height: 16 }}
                  />
                  Require post-completion form
                </label>
                <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px", marginLeft: "24px" }}>
                  Users must fill out a form before completing this task
                </p>
                
                {/* Show error message if form is required but no form node is connected */}
                {hasError && (
                  <div style={{ 
                    marginTop: "8px", 
                    padding: "8px 12px", 
                    backgroundColor: "#fef2f2", 
                    border: "1px solid #fecaca", 
                    borderRadius: "4px",
                    marginLeft: "24px"
                  }}>
                    <div style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: "6px", 
                      color: "#dc2626", 
                      fontSize: "12px",
                      fontWeight: "500"
                    }}>
                      <AlertTriangle size={12} />
                      <span>Missing Form Node</span>
                    </div>
                    <p style={{ 
                      fontSize: "11px", 
                      color: "#dc2626", 
                      marginTop: "4px", 
                      marginBottom: "0",
                      lineHeight: "1.3"
                    }}>
                      This task requires a post-completion form but is not connected to a form node. 
                      Connect this task to a form node to resolve this issue.
                    </p>
                  </div>
                )}
              </div>

              {showAllowMultipleInstancesOption && (
                <div className="node-field">
                  <label style={{ display: "flex", alignItems: "center", fontWeight: 400, fontSize: 15 }} className="text-gray-500 dark:text-white">
                    <input
                      type="checkbox"
                      checked={!!taskData.allowMultipleInstances}
                      onChange={(e) => {
                        const updatedData = {
                          ...taskData,
                          allowMultipleInstances: e.target.checked,
                        }
                        handleChange("allowMultipleInstances", e.target.checked)
                        saveNodeDataToBuilder(updatedData)
                      }}
                      style={{ marginRight: 8, width: 16, height: 16 }}
                    />
                    Allow multiple open instances on loop re-run
                  </label>
                  <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px", marginLeft: "24px" }}>
                    When a looping parent fires this step again, create a new task even if a previous one is still open
                  </p>
                </div>
              )}

              {(taskData.deferrable === 1 ||
                taskData.deferrable === true) &&
                taskData.requirePostCompletionForm &&
                taskData.postCompletionFormId && (
                <div className="node-field" style={{ marginTop: 8, marginLeft: 8 }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontWeight: 400,
                      fontSize: 15,
                    }}
                    className="text-gray-500 dark:text-white"
                  >
                    <input
                      type="checkbox"
                      checked={!!taskData.captureDeferDateInPostCompletionForm}
                      onChange={(e) => {
                        const checked = e.target.checked
                        const updatedData = {
                          ...taskData,
                          captureDeferDateInPostCompletionForm: checked,
                          postCompletionDeferDateFieldId: checked
                            ? taskData.postCompletionDeferDateFieldId
                            : "",
                        }
                        setTaskData(updatedData)
                        updateWorkflowContext(updatedData)
                        saveNodeDataToBuilder(updatedData)
                      }}
                      style={{ marginRight: 8, width: 16, height: 16 }}
                    />
                    Capture defer date in completion form (hide Defer button on task)
                  </label>
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginTop: "4px",
                      marginLeft: "24px",
                    }}
                  >
                    The defer-until date is collected in the form when the user completes
                    the task.
                  </p>
                  {taskData.captureDeferDateInPostCompletionForm && (
                    <div style={{ marginTop: 8, marginLeft: 24 }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: 13,
                          marginBottom: 4,
                        }}
                        className="text-gray-700 dark:text-gray-200"
                      >
                        Defer date field
                      </label>
                      <select
                        value={taskData.postCompletionDeferDateFieldId}
                        onChange={(e) => {
                          const updatedData = {
                            ...taskData,
                            postCompletionDeferDateFieldId: e.target.value,
                          }
                          setTaskData(updatedData)
                          updateWorkflowContext(updatedData)
                          saveNodeDataToBuilder(updatedData)
                        }}
                        className="w-full"
                        style={{
                          padding: "6px 8px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          fontSize: "13px",
                        }}
                      >
                        <option value="">Select a date field...</option>
                        {postCompletionFormDateFields.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.label || f.id}
                          </option>
                        ))}
                      </select>
                      {postCompletionFormDateFields.length === 0 && (
                        <p
                          style={{
                            fontSize: "11px",
                            color: "#9ca3af",
                            marginTop: 4,
                          }}
                        >
                          No date fields found on this form yet. Add a date field to the
                          form template, then reopen this panel.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* {taskData.requirePostCompletionForm && (
                <div className="node-field">
                  <label>Post-Completion Form</label>
                  <select
                    value={taskData.postCompletionFormId}
                    onChange={(e) => handleChange("postCompletionFormId", e.target.value)}
                    className="w-full"
                    style={{ padding: "6px 8px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px" }}
                  >
                    <option value="">Select a form...</option>
                    {getAvailableFormNodes().map((form) => (
                      <option key={form.id} value={form.id}>
                        {form.name}
                      </option>
                    ))}
                  </select>
                  {taskData.postCompletionFormId && (
                    <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                      Selected: {getAvailableFormNodes().find(f => f.id === taskData.postCompletionFormId)?.name}
                    </p>
                  )}
                </div>
              )} */}
            </div>
          )}
        </div>
      )}

      {/* Input handle - top when in auto-layout mode, otherwise left/top based on view mode */}
        <Handle
          type="target"
          position={
            // data.autoLayoutMode && data.handlePosition === 'top-bottom'
            //   ? Position.Top
            //   : (currentWorkflow?.viewMode === "tree" ? Position.Top : Position.Left)
            Position.Top
          }
          id="b"
        />

      {/* Output handle - bottom when in auto-layout mode, otherwise right/bottom based on view mode */}
        <Handle
          type="source"
          position={
            // data.autoLayoutMode && data.handlePosition === 'top-bottom'
            //   ? Position.Bottom
            //   : (currentWorkflow?.viewMode === "tree" ? Position.Bottom : Position.Right)
            Position.Bottom
          }

          id="a"
        />
    </div>
  )
}

export default TaskNode
